import CStack from "../../components/CStack";
import UserInfo from "../../components/UserInfo";
import BaseControl from "../../components/BaseControl";
import UserListView from "../../containers/Users/UserListView";
import { useUserStore } from "../../api/users/store";
import Loading from "../../components/Loading";
import { ClientInfo, getClientHeader, USTATUS_ACTIVE, USTATUS_BLOCKED } from "../../api/users/data";
import Label from "../../components/Label";
import AccountList from "../../containers/Users/AccountList";
import Searchbar from "../../components/Searchbar";
import DeviceList from "../../containers/Users/DeviceList";
import useUXStore from "../../api/ux/store";
import { useCompanySettingsStore } from "../../api/company/store";
import { useEffect, useState } from "react";
import { Button, Container, Alert, Spinner, Stack } from "react-bootstrap";
import { CATEGORY_MOBILE_BANKING } from "../../api/company/data";
import CompanySettings from "../../containers/CompanySettings/CompanySettings";
import TransactionHistory from "../../components/TransactionHistory";
import ConfirmationInput from "../../components/ConfirmationInput";
import axios from "axios";
import { ENDPOINT_COMPANY_SETTINGS_TEST_HOST } from "../../constants";
import { ReactComponent as BlockIcon } from "../../svgs/block.svg";
import { ReactComponent as UnblockIcon } from "../../svgs/unblock.svg";
import { ReactComponent as UnregisterIcon } from "../../svgs/unregister.svg";

type UserPageProps = {
    user : ClientInfo
}

const UserPage:React.FC<UserPageProps> = ({user}) => {
    const {deleteUser, blockUser, unblockUser, currentUser, setCurrentUser} = useUserStore(state => state)
    const {refreshDialog, addToast, popPage} = useUXStore(state => state)
    
    // Use currentUser from store if available, otherwise use prop
    const displayUser = currentUser || user
    
    const handleDelete = () => {
        const userName = `${user.FirstName} ${user.LastName}`
        const confirmText = "UNREGISTER"

        refreshDialog({
            id: "userDelete",
            title: "Unregister User",
            visible: true,
            confirmActive: false,
            confirmContent: "Unregister",
            content: (
                <ConfirmationInput
                    message={`Are you sure you want to delete user ${userName}?`}
                    warningMessage="This action will unregister the user from the system. The user's bank data will be retained but they will no longer have access."
                    confirmText={confirmText}
                    onValidChange={(isValid) => {
                        refreshDialog({
                            id: "userDelete",
                            confirmActive: isValid
                        })
                    }}
                />
            ),
            onConfirm: () => {
                console.log(user)
                deleteUser(user.UserId).then(() =>
                    addToast("User Deleted",`User ${userName} has been deleted successfully.`,)
                ).catch( err => {
                    console.error(err.message)
                    addToast("Delete Failed", "Failed to delete user",)
                })
                popPage()
            },
        }, true)
    }

    const handleBlock = () => {
        const userName = `${displayUser.FirstName} ${displayUser.LastName}`
        const confirmText = "BLOCK"

        refreshDialog({
            id: "userBlock",
            title: "Block User",
            visible: true,
            confirmActive: false,
            confirmContent: "Block User",
            content: (
                <ConfirmationInput
                    message={`Are you sure you want to block user ${userName}?`}
                    warningMessage="This action will prevent the user from logging into the system. You can unblock them later if needed."
                    confirmText={confirmText}
                    onValidChange={(isValid) => {
                        refreshDialog({
                            id: "userBlock",
                            confirmActive: isValid
                        })
                    }}
                />
            ),
            onConfirm: () => {
                blockUser(displayUser.ClientId).then(() => {
                    addToast("User Blocked",`User ${userName} has been blocked successfully.`)
                    // Update the current user to reflect the status change
                    setCurrentUser({...displayUser, Status: USTATUS_BLOCKED})
                }).catch( err => {
                    console.error(err.message)
                    addToast("Block Failed", "Failed to block user")
                })
            },
        }, true)
    }

    const handleUnblock = () => {
        const userName = `${displayUser.FirstName} ${displayUser.LastName}`
        const confirmText = "UNBLOCK"

        refreshDialog({
            id: "userUnblock",
            title: "Unblock User",
            visible: true,
            confirmActive: false,
            confirmContent: "Unblock User",
            content: (
                <ConfirmationInput
                    message={`Are you sure you want to unblock user ${userName}?`}
                    warningMessage="This action will allow the user to log into the system again."
                    confirmText={confirmText}
                    onValidChange={(isValid) => {
                        refreshDialog({
                            id: "userUnblock",
                            confirmActive: isValid
                        })
                    }}
                />
            ),
            onConfirm: () => {
                unblockUser(displayUser.ClientId).then(() => {
                    addToast("User Unblocked",`User ${userName} has been unblocked successfully.`)
                    // Update the current user to reflect the status change
                    setCurrentUser({...displayUser, Status: USTATUS_ACTIVE})
                }).catch( err => {
                    console.error(err.message)
                    addToast("Unblock Failed", "Failed to unblock user")
                })
            },
        }, true)
    }
    // Get company settings to check if deletion is allowed
    const { settings } = useCompanySettingsStore(state => state)
    const enableDelete = settings?.settings?.security?.data?.allow_user_deletion ?? true
    //console.log("UserPage render", displayUser, enableDelete)
    return <>
        <UserInfo user={displayUser}/>
        <CStack>
            <BaseControl text="Status">
                {/* <AccountStatusDropdown selected={displayUser.Status}/> */}
                <Label>{displayUser.Status}</Label>
            </BaseControl>
            <BaseControl text="Fund Transfer Limit">
                <Label textAlign="end">{displayUser.FundTransferLimit.toString()}</Label>
            </BaseControl>
        </CStack>

        <AccountList />

        {/* Transaction History Section */}
        <TransactionHistory clientId={displayUser.ClientId} limit={5} />

        {/* Device List Section */}
        <DeviceList />

        <CStack header="Actions">
            {displayUser.Status.toLowerCase() === USTATUS_ACTIVE && (
                <BaseControl
                    contentSize={2}
                    text="Block User"
                    caption="This will prevent the user from logging in."
                    icon={BlockIcon}
                >
                    <Button size="sm" onClick={handleBlock} variant="outline-danger">Block</Button>
                </BaseControl>
            )}

            {displayUser.Status.toLowerCase() === USTATUS_BLOCKED && (
                <BaseControl
                    contentSize={2}
                    text="Unblock User"
                    caption="This will allow the user to log in again."
                    icon={UnblockIcon}
                >
                    <Button size="sm" onClick={handleUnblock} variant="outline-success">Unblock</Button>
                </BaseControl>
            )}
            { enableDelete && <BaseControl
                contentSize={2}
                text="Unregister User"
                caption="Remove user access but bank data will retain."
                icon={UnregisterIcon}
            >
                <Button size="sm" onClick={handleDelete} variant="outline-danger">Unregister</Button>
            </BaseControl>}
        </CStack>
    </>
}

const Users = () => {
    const {users, 
        loading, 
        retrieveUsers, 
        clearUsers,
        setCurrentUser, 
        canLoadMoreUsers,
        retrieveUserQuery,
        error,
        clearError
    } = useUserStore( state => state)
    const {pushPage} = useUXStore( state => state)
    const {settings, loading: settingsLoading, retrieveSettings} = useCompanySettingsStore(state => state)
    const [showSettings, setShowSettings] = useState(false)
    const [testingConnection, setTestingConnection] = useState(false)
    const [testResult, setTestResult] = useState<{success: boolean; message: string} | null>(null)
    const [currentHost, setCurrentHost] = useState<string | null>(null)

    useEffect(() => {
        if (!settings && !settingsLoading) {
            retrieveSettings()
        }
    }, [settings, settingsLoading, retrieveSettings])
    
    // Check if host URL is configured
    const hostUrl = settings?.settings?.[CATEGORY_MOBILE_BANKING]?.data?.host_url
    const hasHostUrl = hostUrl && hostUrl.trim() !== ""
    
    // Only retrieve users if host URL is configured
    useEffect(() => {
        if (hasHostUrl 
            && (users === null || users.length === 0 || hostUrl !== currentHost)
            && !loading) {
            //console.log("Retrieving users for host URL:", hostUrl)
            clearError()
            setCurrentHost(hostUrl)
            retrieveUsers(retrieveUserQuery)
        }
    }, [hasHostUrl, users, retrieveUsers, retrieveUserQuery, hostUrl, currentHost, loading, clearError])
    
    // Clear users when host URL is removed
    useEffect(() => {
        if (!hasHostUrl && users !== null) {
            clearUsers()
        }
    }, [hasHostUrl])

    const onSelected = (user:ClientInfo) => {
        setCurrentUser(user)
        pushPage(getClientHeader(user), <UserPage user={user}/>)
    }
    const reload = () => {
        clearUsers()
        retrieveUsers(retrieveUserQuery)
    }
    
    const handleSettingsClose = () => {
        setShowSettings(false)
        // Re-check settings after closing
        retrieveSettings()
    }
    
    const testConnection = async () => {
        if (!hostUrl) return
        
        setTestingConnection(true)
        setTestResult(null)
        
        try {
            const response = await axios.post(ENDPOINT_COMPANY_SETTINGS_TEST_HOST, {
                host_url: hostUrl,
                health_check_path: settings?.settings?.[CATEGORY_MOBILE_BANKING]?.data?.health_check_path || ""
            })
            
            const data = response.data.Msg
            setTestResult({
                success: data.success,
                message: data.success ? "Connection successful!" : "Connection failed: " + (data.error || "Unknown error")
            })
            
            // If test successful, retry loading users
            if (data.success) {
                setTimeout(() => {
                    clearError()
                    reload()
                }, 1500)
            }
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.msg || "Failed to test connection"
            })
        } finally {
            setTestingConnection(false)
        }
    }
    
    // Show loading while checking settings
    if (settingsLoading) {
        return <Loading expandHeight/>
    }
    
    // Show setup prompt if host URL is not configured
    if (!hasHostUrl) {
        return (
            <>
                <Container className="text-center py-5">
                    <Alert variant="warning" className="mb-4">
                        <Alert.Heading>Configuration Required</Alert.Heading>
                        <p>
                            The Mobile Banking Host URL is not configured. 
                            Please set up the company settings to use the user management features.
                        </p>
                    </Alert>
                    <Button 
                        variant="outline-primary"
                        size="lg"
                        onClick={() => setShowSettings(true)}
                    >
                        Configure
                    </Button>
                </Container>
                <CompanySettings show={showSettings} onHide={handleSettingsClose} />
            </>
        )
    }
    
    const showLoading = loading && users === null 
    const isConnectionError = error && error?.startsWith('CONNECTION_ERROR:')
    
    return <>
        <Searchbar onReload={reload} clear={clearUsers} search={retrieveUsers}/>
        
        {error && (
            <Alert 
                variant={isConnectionError ? "danger" : "warning"} 
                dismissible 
                onClose={clearError}
                className="mx-3 mt-3"
            >
                <Alert.Heading>
                    {isConnectionError ? "Connection Error" : "Error"}
                </Alert.Heading>
                <p>{isConnectionError && error.replace('CONNECTION_ERROR: ', '')}</p>
                {isConnectionError && (
                    <div>
                        <p className="mb-2">Current Host URL: <strong>{hostUrl || 'Not configured'}</strong></p>
                        <Stack direction="horizontal" gap={2}>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => setShowSettings(true)}
                            >
                                Check Settings
                            </Button>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={testConnection}
                                disabled={testingConnection}
                            >
                                {testingConnection ? (
                                    <>
                                        <Spinner size="sm" className="me-1" />
                                        Testing...
                                    </>
                                ) : (
                                    "Test Connection"
                                )}
                            </Button>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => {
                                    clearError()
                                    setTestResult(null)
                                    reload()
                                }}
                            >
                                Retry
                            </Button>
                        </Stack>
                        {testResult && (
                            <Alert 
                                variant={testResult.success ? "success" : ""} 
                                className="mt-2 mb-0"
                                dismissible
                                onClose={() => setTestResult(null)}
                            >
                                <h5>Test Result</h5>
                                {testResult.message}
                            </Alert>
                        )}
                    </div>
                )}
            </Alert>
        )}
        
        {!showLoading && users !== null && users.length === 0 && !error && <Label>Search results 0</Label>}
        {showLoading && <Loading expandHeight/>}
        {!showLoading && !error && <UserListView users={users} 
            showLoadMore={canLoadMoreUsers}
            onLoadMore={() => retrieveUsers(retrieveUserQuery)}
            onSelected={onSelected}/>}
        
        <CompanySettings show={showSettings} onHide={handleSettingsClose} />
    </>
}

export default Users;